/**
 * Export de vault: citește și decriptează în browser toate credentialele din
 * spațiul curent la care userul are acces. Criptarea fișierului cu parola de
 * export e în `format.ts`; aici nu se scrie nimic nicăieri.
 */
import { credentialsApi } from '@/api/credentials';
import { errorMessage } from '@/api/client';
import { decryptCredential, type KeyPair } from '@/crypto/sodium';
import { EXPORT_FILE_SUFFIX } from '@/features/vault/transfer/format';
import type { TransferCredential } from '@/features/vault/transfer/types';

export interface TransferProgress {
  done: number;
  total: number;
  currentLabel: string;
}

export interface CollectResult {
  credentials: TransferCredential[];
  /** Credentiale care nu au putut fi citite sau decriptate — lipsesc din export. */
  failed: Array<{ title: string; message: string }>;
  cancelled: boolean;
}

export interface CollectOptions {
  tenantId: string;
  keyPair: KeyPair;
  /** Include și credentialele break-glass; fiecare citire a lor intră în audit. */
  includeBreakGlass?: boolean;
  onProgress?: (p: TransferProgress) => void;
  signal?: AbortSignal;
}

export async function collectVault(opts: CollectOptions): Promise<CollectResult> {
  // Lista obișnuită nu conține credentialele break-glass — vin din ruta lor.
  const summaries = await credentialsApi.list(opts.tenantId);
  if (opts.includeBreakGlass) {
    summaries.push(...(await credentialsApi.listBreakGlass(opts.tenantId)));
  }
  const result: CollectResult = { credentials: [], failed: [], cancelled: false };

  for (const [index, summary] of summaries.entries()) {
    if (opts.signal?.aborted) {
      result.cancelled = true;
      break;
    }
    opts.onProgress?.({ done: index, total: summaries.length, currentLabel: summary.title });
    try {
      // `noTrack`: exportul nu e o folosire a credentialului și nu trebuie să-i
      // umfle contorul de accesări. Citirea unui break-glass se auditează oricum.
      const detail = await credentialsApi.get(summary.id, { noTrack: true });
      const payload = await decryptCredential(detail, opts.keyPair);
      result.credentials.push({
        type: detail.type,
        title: detail.title,
        url: detail.url,
        username: detail.username,
        expires_at: detail.expires_at,
        meta: detail.meta,
        payload,
      });
    } catch (err) {
      result.failed.push({ title: summary.title, message: errorMessage(err) });
    }
  }
  opts.onProgress?.({ done: summaries.length, total: summaries.length, currentLabel: '' });
  return result;
}

/** `cerbes-<spațiu>-<AAAA-LL-ZZ>.cerbes-export.json` */
export function exportFilename(tenantSlug: string, now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const slug = tenantSlug.replace(/[^a-z0-9-]/gi, '') || 'vault';
  return `cerbes-${slug}-${date}${EXPORT_FILE_SUFFIX}`;
}
